import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Animated, Easing, StyleSheet, type View } from 'react-native';
import { useTheme, BORDER_RADIUS, ELEVATION, Z_INDEX, durations } from '../../theme';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { useEscapeToClose } from '../../utils/escapeStack';
import { useAnchoredPosition, type Placement } from '../../hooks/useAnchoredPosition';
import { DialogPortal } from './DialogPortal';

export interface PopoverProps {
  visible: boolean;
  anchorRef: React.RefObject<unknown>;
  onClose: () => void;
  placement: Placement;
  children: React.ReactNode;
  testID?: string;
  accessibilityLabel?: string;
  /** 'menu' (default) for a list of actions the arrow keys/tab move through;
   *  'dialog' for a short non-menu preview. Never `aria-modal` — a popover
   *  never traps focus or blocks the page, per panel-system.md's rule 5. */
  role?: 'menu' | 'dialog';
  gap?: number;
  /** Floor for the popover's left edge, in px from the viewport's left. Rail
   *  popovers pass the sidebar's width so the menu opens past the rail
   *  instead of over it (panel-system.md, "The 64px rail"). */
  minLeft?: number;
  /** Draws a small caret on the popover's left edge, level with the
   *  anchor's center, so a menu opened beside its trigger reads as coming
   *  from that trigger (the rail's "+" menu sits right under the map's
   *  search bar, and without it looked like a search dropdown; review
   *  note 9). Only meaningful for the `right-*` placements. */
  caret?: boolean;
}

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Closes on any pointerdown outside both `containerRef` (the popover itself)
 * and `anchorRef` (its trigger — the trigger's own onPress toggles the
 * popover, so a click there must never also register as "outside" or it
 * would race that toggle: close, then immediately reopen, or close on the
 * very press meant to open it).
 *
 * A standalone hook, not inlined in Popover, so the interaction can be
 * exercised directly against real DOM nodes in jest: this repo's jest-expo
 * preset renders RN host components without a real DOM (no
 * `getBoundingClientRect`/`contains`), so a fully rendered `<Popover>`'s own
 * ref never resolves to a real element the way it does under
 * react-native-web in production. Feeding this hook plain `{ current: div }`
 * refs sidesteps that gap — see __tests__/components/Popover.test.tsx.
 */
export function usePopoverOutsideClose(
  active: boolean,
  containerRef: React.RefObject<unknown>,
  anchorRef: React.RefObject<unknown>,
  onClose: () => void
) {
  useEffect(() => {
    if (!active) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      const popoverNode = containerRef.current as unknown as {
        contains?: (n: Node) => boolean;
      } | null;
      const anchorNode = anchorRef.current as unknown as { contains?: (n: Node) => boolean } | null;
      if (typeof popoverNode?.contains === 'function' && popoverNode.contains(target)) return;
      if (typeof anchorNode?.contains === 'function' && anchorNode.contains(target)) return;
      onClose();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [active, containerRef, anchorRef, onClose]);
}

/**
 * Web-only anchored popover: a short menu or preview anchored to its
 * trigger, no scrim, dismissed by Escape or a click elsewhere
 * (panel-system.md, "The rule for new UI", item 5). Portals to
 * document.body (DialogPortal) so it always escapes a clipping ancestor —
 * the rail's own nav ScrollView is exactly the kind of container that cut
 * off SidebarNav's hover tooltip for the same reason.
 */
export function Popover({
  visible,
  anchorRef,
  onClose,
  placement,
  children,
  testID,
  accessibilityLabel,
  role = 'menu',
  gap = 8,
  minLeft,
  caret = false,
}: PopoverProps) {
  const { colors } = useTheme();
  const reducedMotion = useReducedMotion();
  const containerRef = useRef<View>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  // Opacity/scale in one Animated.Value, same shape as DockedPanel's slideIn
  // — 0 until positioned (so there's nothing to see while off-screen at
  // -9999,-9999 pre-measurement), then animated to 1.
  const enter = useRef(new Animated.Value(0)).current;

  const anchored = useAnchoredPosition(anchorRef, size, placement, gap);
  const position =
    anchored && minLeft !== undefined && anchored.left < minLeft
      ? { ...anchored, left: minLeft }
      : anchored;
  const positioned = position !== null;

  // Measure the popover itself once it's in the DOM, and again if its
  // content changes size — useAnchoredPosition needs a real size to place it
  // against, and this component is the only thing that knows its content.
  useLayoutEffect(() => {
    if (!visible) return;
    const node = containerRef.current as unknown as HTMLElement | null;
    if (typeof node?.getBoundingClientRect !== 'function') return;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      setSize({ width: rect.width, height: rect.height });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  usePopoverOutsideClose(visible, containerRef, anchorRef, onClose);
  useEscapeToClose(visible, onClose);

  // Focus the first focusable item on open; return focus to the trigger on
  // close (including a close driven by the caller unmounting us, not just a
  // dismiss the user drove from inside the popover).
  useEffect(() => {
    if (!visible) return;
    const node = containerRef.current as unknown as HTMLElement | null;
    // Captured now, not read again in the cleanup: the anchor is the same
    // trigger button for the whole time the popover can be open, so there's
    // no risk of this going stale before the cleanup runs.
    const anchorNode = anchorRef.current as unknown as HTMLElement | null;
    const raf = requestAnimationFrame(() => {
      const first = node?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      first?.focus();
    });
    return () => {
      cancelAnimationFrame(raf);
      anchorNode?.focus?.();
    };
  }, [visible, anchorRef]);

  // Enter only — an exit animation needs a keep-mounted-while-leaving state
  // (panel-system.md flags this for DockedPanel); a popover doesn't need
  // one, "Exit instant is fine" per this phase's spec, so the caller just
  // unmounts us and there's nothing left to animate out.
  useEffect(() => {
    if (!positioned) {
      enter.setValue(0);
      return;
    }
    const animation = Animated.timing(enter, {
      toValue: 1,
      duration: reducedMotion ? 0 : durations.quick,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    });
    animation.start();
    return () => animation.stop();
  }, [positioned, reducedMotion, enter]);

  if (!visible) return null;

  // Read at render: the popover re-renders once it's positioned, and a
  // rail button doesn't move while its menu is open.
  const anchorRect = caret
    ? (anchorRef.current as unknown as HTMLElement | null)?.getBoundingClientRect?.()
    : undefined;
  const caretTop =
    anchorRect && position && size
      ? Math.min(
          Math.max(
            anchorRect.top + anchorRect.height / 2 - CARET_SIZE / 2,
            position.top + CARET_INSET
          ),
          position.top + size.height - CARET_SIZE - CARET_INSET
        )
      : null;

  return (
    <DialogPortal>
      <Animated.View
        ref={containerRef}
        testID={testID}
        accessibilityLabel={accessibilityLabel}
        // `role` accepts 'dialog', which RN's AccessibilityRole union doesn't
        // — set it (and its aria-label alias) as a raw DOM attribute instead
        // of accessibilityRole, the same escape hatch SidebarNav uses for
        // `dataSet`.
        {...({ role, 'aria-label': accessibilityLabel } as object)}
        style={[
          styles.popover,
          ELEVATION.soft,
          {
            backgroundColor: colors.surfaceElevated,
            borderColor: colors.border,
            borderRadius: BORDER_RADIUS.md,
            top: position?.top ?? -9999,
            left: position?.left ?? -9999,
            zIndex: Z_INDEX.popover,
            opacity: enter,
            transform: [
              { scale: enter.interpolate({ inputRange: [0, 1], outputRange: [0.98, 1] }) },
            ],
          },
        ]}>
        {children}
      </Animated.View>
      {caretTop !== null && position && (
        // Outside the popover's own clip (overflow: hidden). A rotated
        // square half over the popover's left edge: its inner half, in the
        // popover's own fill, covers that stretch of border, so only a
        // bordered point shows. Same technique as MapPostHoverCard's caret.
        <Animated.View
          testID={testID ? `${testID}-caret` : undefined}
          pointerEvents="none"
          style={[
            styles.caret,
            {
              top: caretTop,
              left: position.left - CARET_SIZE / 2 + 1,
              backgroundColor: colors.surfaceElevated,
              borderColor: colors.border,
              zIndex: Z_INDEX.popover,
              opacity: enter,
            },
          ]}
        />
      )}
    </DialogPortal>
  );
}

const CARET_SIZE = 10;
/** Keeps the caret clear of the popover's rounded corners. */
const CARET_INSET = BORDER_RADIUS.md;

const styles = StyleSheet.create({
  popover: {
    position: 'fixed' as 'absolute',
    borderWidth: 1,
    // Clips item rows to the rounded corners; without it their square
    // backgrounds showed past the radius in dark mode. box-shadow on web
    // still draws outside the clip.
    overflow: 'hidden',
  },
  caret: {
    position: 'fixed' as 'absolute',
    width: CARET_SIZE,
    height: CARET_SIZE,
    borderLeftWidth: 1,
    borderBottomWidth: 1,
    transform: [{ rotate: '45deg' }],
  },
});
